import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  FiUsers, 
  FiCreditCard, 
  FiUserPlus, 
  FiTarget, 
  FiTrendingUp, 
  FiDownload, 
  FiFilter, 
  FiSearch,
  FiUser
} from 'react-icons/fi';
import { BsArrowRepeat, BsCashStack } from 'react-icons/bs';
import { Icon } from "@iconify/react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";

/* ─── Styles ─── */
const s = {
  root: { padding: "20px 24px 40px", background: "#f1f5f9", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
  headerCard: { background: "#fff", borderRadius: 16, padding: "20px 24px", display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", marginBottom: 20 },
  headerTitleWrap: { display: "flex", alignItems: "center", gap: 12 },
  headerIconWrap: { background: "#eff6ff", padding: 12, borderRadius: 12, color: "#3b82f6" },
  headerTitle: { margin: 0, fontWeight: 800, fontSize: "1.4rem", color: "#0f172a" },
  headerSub: { margin: "2px 0 0", color: "#64748b", fontSize: "0.85rem" },
  btnGroup: { display: "flex", gap: "12px", flexWrap: "wrap" },
  btnOutlined: { display: "flex", alignItems: "center", gap: 6, padding: "8px 18px", border: "1.5px solid #e2e8f0", background: "#fff", borderRadius: 9, fontSize: "0.85rem", fontWeight: 700, color: "#475569", cursor: "pointer", transition: "all 0.2s" },
  btnSave: { display: "flex", alignItems: "center", gap: 6, padding: "8px 20px", background: "linear-gradient(135deg,#16632b,#1da144)", border: "none", borderRadius: 9, fontSize: "0.85rem", fontWeight: 700, color: "#fff", cursor: "pointer", transition: "all 0.2s" },
  filterBox: { background: "#fff", borderRadius: 16, padding: "20px 24px", border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", marginBottom: 24, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16 },
  label: { display: "block", fontSize: "0.75rem", fontWeight: 700, color: "#475569", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.04em" },
  input: { width: "100%", padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: 9, fontSize: "0.87rem", color: "#1e293b", background: "#f8fafc", outline: "none", fontFamily: "inherit", boxSizing: "border-box" },
  summaryGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px", marginBottom: "24px" },
  sumCard: { padding: "20px", borderRadius: 16, color: "#fff", display: "flex", flexDirection: "column", gap: 10, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" },
  sumLabel: { fontSize: "0.85rem", fontWeight: 700, opacity: 0.9, textTransform: "uppercase", letterSpacing: "0.05em" },
  sumValue: { fontSize: "1.8rem", fontWeight: 900, lineHeight: 1 },
  sumIcon: { background: "rgba(255,255,255,0.2)", padding: 8, borderRadius: 10, display: "inline-flex" },
  card: { background: "#fff", borderRadius: 16, border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", overflow: "hidden" },
  toolbar: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid #f1f5f9", flexWrap: "wrap", gap: 10 },
  searchWrap: { position: "relative", display: "flex", alignItems: "center" },
  searchIcon: { position: "absolute", left: 10, fontSize: 15, color: "#94a3b8", pointerEvents: "none" },
  searchInput: { padding: "7px 30px 7px 32px", border: "1.5px solid #e2e8f0", borderRadius: 9, fontSize: "0.82rem", color: "#334155", background: "#f8fafc", outline: "none", width: 240, fontFamily: "inherit" },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" },
  th: { padding: "12px 16px", background: "#f8fafc", color: "#64748b", fontWeight: 700, fontSize: "0.71rem", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1.5px solid #f1f5f9", whiteSpace: "nowrap", textAlign: "left" },
  td: { padding: "12px 16px", borderBottom: "1px solid #f8fafc", color: "#334155", verticalAlign: "middle" },
  pgInfo: { fontSize: "0.78rem", color: "#64748b" },
  pgBtns: { display: "flex", gap: 4 },
  pgBtn: { minWidth: 30, height: 30, border: "1px solid #e2e8f0", background: "#fff", borderRadius: 7, cursor: "pointer", fontSize: "0.78rem", color: "#475569", display: "flex", alignItems: "center", justifyContent: "center" },
};

/* ─── Helpers ─── */
const formatCurrency = (amt) => {
  if (!amt) return "₹0";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(amt);
};

const formatNumber = (num) => {
  if (!num) return "0";
  return new Intl.NumberFormat("en-IN").format(num);
};

const SortIcon = ({ col }) => {
  if (!col.getCanSort()) return null;
  const dir = col.getIsSorted();
  return <span style={{ marginLeft: 4, opacity: dir ? 1 : 0.35, fontSize: 11 }}>{dir === "asc" ? "▲" : dir === "desc" ? "▼" : "⇅"}</span>;
};

const StatusBadge = ({ status }) => {
  const isAct = status?.toLowerCase() === 'active';
  return (
    <span style={{ 
      background: isAct ? "#dcfce7" : "#f1f5f9", 
      color: isAct ? "#16a34a" : "#64748b",
      padding: "3px 10px", borderRadius: 100, fontSize: "0.68rem", fontWeight: 800, textTransform: "uppercase",
      border: `1px solid ${isAct ? "#dcfce7" : "#e2e8f0"}`
    }}>
      {status || "UNKNOWN"}
    </span>
  );
};

const RoleBadge = ({ role }) => {
  const map = {
    admin: { bg: "#fef3c7", color: "#d97706" },
    agent: { bg: "#e0f2fe", color: "#0284c7" },
    user: { bg: "#eff6ff", color: "#3b82f6" }
  };
  const code = role?.toLowerCase() || "user";
  const m = map[code] || map.user;
  return (
    <span style={{ 
      background: m.bg, color: m.color,
      padding: "3px 8px", borderRadius: 6, fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase" 
    }}>
      {code}
    </span>
  );
};


/* ─── Reusable Data Grid ─── */
const DataTable = ({ columns, data, toolbarExtra }) => {
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 15 });

  const table = useReactTable({
    data, columns, state: { globalFilter, sorting, pagination },
    onGlobalFilterChange: setGlobalFilter, onSortingChange: setSorting, onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(), getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(), getPaginationRowModel: getPaginationRowModel(),
  });

  const { pageIndex, pageSize } = table.getState().pagination;
  const pageCount = table.getPageCount();
  const rawCount = table.getFilteredRowModel().rows.length;

  return (
    <div style={s.card}>
      <div style={s.toolbar}>
        <div style={s.searchWrap}>
          <Icon icon="ion:search-outline" style={s.searchIcon} />
          <input
            style={s.searchInput}
            placeholder="Search Identity or ID..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
          />
        </div>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          {toolbarExtra}
          <select style={{...s.input, width: "auto", minWidth: 80}} value={pageSize} onChange={e=>table.setPageSize(Number(e.target.value))}>
            {[15,30,50,100].map(n=><option key={n} value={n}>Show {n}</option>)}
          </select>
        </div>
      </div>

      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            {table.getHeaderGroups().map(hg => (
              <tr key={hg.id}>
                {hg.headers.map(header => (
                  <th key={header.id} style={{ ...s.th, cursor: header.column.getCanSort() ? "pointer" : "default" }} onClick={header.column.getToggleSortingHandler()}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    <SortIcon col={header.column} />
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {rawCount > 0 ? table.getRowModel().rows.map((row, idx) => (
              <tr key={row.id} style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}>
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id} style={s.td}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            )) : (
              <tr><td colSpan={columns.length} style={{ padding: "40px", textAlign: "center", color: "#94a3b8" }}>No ecosystem nodes matched filter criteria.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderTop: "1px solid #f1f5f9" }}>
        <div style={s.pgInfo}>Showing {rawCount > 0 ? pageIndex*pageSize+1 : 0}–{Math.min((pageIndex+1)*pageSize, rawCount)} of {rawCount}</div>
        <div style={s.pgBtns}>
           <button style={s.pgBtn} onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>‹</button>
           <span style={{ fontSize: "0.78rem", fontWeight: 600, padding: "0 8px", alignSelf: "center", color: "#475569" }}>
             Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount() || 1}
           </span>
           <button style={s.pgBtn} onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>›</button>
        </div>
      </div>
    </div>
  );
};


/* ─── Column Definitions ─── */
const userCols = [
  { 
    id: "identity", 
    header: "Identity & ID", 
    accessorFn: row => `${row.name} ${row.user_id}`,
    cell: ({row}) => (
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#eff6ff", color: "#3b82f6", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "0.85rem" }}>
          {row.original.name?.charAt(0)?.toUpperCase()}
        </div>
        <div>
          <div style={{ fontWeight: 700, color: "#1e293b" }}>{row.original.name}</div>
          <div style={{ fontSize: "0.7rem", fontFamily: "monospace", color: "#64748b" }}>#{row.original.user_id}</div>
        </div>
      </div>
    )
  },
  { 
    id: "statusRole", 
    header: "Status & Role", 
    accessorFn: row => `${row.status} ${row.role}`,
    cell: ({row}) => (
      <div style={{ display: "flex", flexDirection: "column", gap: 5, alignItems: "flex-start" }}>
        <StatusBadge status={row.original.status} />
        <RoleBadge role={row.original.role} />
      </div>
    )
  },
  { 
    id: "wallet", 
    header: "Wallet Fluidity", 
    accessorFn: row => row.wallet_balance,
    cell: ({row}) => (
      <div style={{ textAlign: "right" }}>
        <div style={{ fontWeight: 800, color: "#3b82f6", fontFamily: "monospace" }}>{formatCurrency(row.original.wallet_balance)}</div>
        <div style={{ fontSize: "0.7rem", color: "#64748b" }}>{row.original.total_referrals} Referrals</div>
      </div>
    )
  },
  { 
    id: "analytics", 
    header: "Analytics (Bet/Win)", 
    enableSorting: false,
    cell: ({row}) => (
      <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "0.65rem", color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", marginBottom: 2 }}>Bet</div>
            <div style={{ fontSize: "0.78rem", fontWeight: 700, fontFamily: "monospace", color: "#334155" }}>{formatCurrency(row.original.total_bet_amount)}</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "0.65rem", color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", marginBottom: 2 }}>Win</div>
            <div style={{ fontSize: "0.78rem", fontWeight: 800, fontFamily: "monospace", color: "#16a34a", padding: "1px 6px", background: "#dcfce7", borderRadius: 4 }}>{formatCurrency(row.original.total_win_amount)}</div>
          </div>
      </div>
    )
  },
  { 
    id: "pl", 
    header: "P/L Impact", 
    accessorFn: row => row.net_profit_loss,
    cell: ({row}) => {
      const pn = row.original.net_profit_loss || 0;
      const isPos = pn >= 0;
      return (
        <div style={{ textAlign: "right" }}>
          <div style={{ fontWeight: 800, color: isPos ? "#16a34a" : "#dc2626", fontFamily: "monospace" }}>
            {isPos ? "+" : ""}{formatCurrency(pn)}
          </div>
          <div style={{ fontSize: "0.7rem", color: "#64748b" }}>Withdraw: {formatCurrency(row.original.total_withdraw)}</div>
        </div>
      )
    }
  },
  { 
    id: "date", 
    header: "Registry Date", 
    accessorFn: row => new Date(row.created_at).getTime(),
    cell: ({row}) => {
      const d = new Date(row.original.created_at);
      return (
        <div style={{ textAlign: "right" }}>
          <div style={{ fontWeight: 600, color: "#475569", fontSize: "0.78rem" }}>{d.toLocaleDateString('en-IN', {day:'2-digit', month:'short', year:'numeric'})}</div>
          <div style={{ fontSize: "0.7rem", color: "#94a3b8" }}>{d.toLocaleTimeString('en-IN', {hour:'2-digit', minute:'2-digit'})}</div>
        </div>
      )
    }
  }
];


/* ─── Main Layer ─── */
export const AllUserReportLayerList = () => {
  const [dashboardTiles, setDashboardTiles] = useState({
    total_users: 0, total_wallet_balance: 0, total_referrals: 0,
    total_bet_amount: 0, total_win_amount: 0, total_withdraw_approved: 0
  });

  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    status: '', role: '',
    minWalletBalance: '', maxWalletBalance: '',
    minTotalReferrals: '', maxTotalReferrals: '',
    minBetAmount: '', maxBetAmount: ''
  });

  const [filteredData, setFilteredData] = useState([]);

  const getAuthToken = () => {
    return localStorage.getItem('authToken') || localStorage.getItem('token') || sessionStorage.getItem('token');
  };

  const fetchUserReportData = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const res = await axios.post('https://opiniongames.net/api/super-admin/user-master-report', {}, {
        headers: { Authorization: token?.startsWith('Bearer') ? token : `Bearer ${token}` }
      });
      if (res.data?.status) {
        setDashboardTiles(res.data.dashboard_tiles);
        setReportData(res.data.report_data);
        setFilteredData(res.data.report_data);
      } else {
        toast.error(res.data.msg || "Failed to fetch user reports");
      }
    } catch (err) {
      toast.error("Failed to sync user ecosystem");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserReportData();
    // eslint-disable-next-line
  }, []);

  // Pre-filter logic (TanStack handles text/global internally, but numeric range filters are passed here)
  useEffect(() => {
    let filtered = [...reportData];
    if (filters.status) filtered = filtered.filter(u => u.status === filters.status);
    if (filters.role) filtered = filtered.filter(u => u.role === filters.role);
    
    // Numeric filters
    const checkRange = (val, min, max) => {
      if (min && Number(val) < Number(min)) return false;
      if (max && Number(val) > Number(max)) return false;
      return true;
    };
    
    filtered = filtered.filter(u => checkRange(u.wallet_balance, filters.minWalletBalance, filters.maxWalletBalance));
    filtered = filtered.filter(u => checkRange(u.total_referrals, filters.minTotalReferrals, filters.maxTotalReferrals));
    filtered = filtered.filter(u => checkRange(u.total_bet_amount, filters.minBetAmount, filters.maxBetAmount));
    
    setFilteredData(filtered);
  }, [filters, reportData]);


  return (
    <div style={s.root}>
      {/* Top Header Card */}
      <div style={s.headerCard}>
        <div style={s.headerTitleWrap}>
          <div style={s.headerIconWrap}><FiUsers size={24} /></div>
          <div>
            <h4 style={s.headerTitle}>User Master Intelligence</h4>
            <p style={s.headerSub}>Total Ecological Participants: {formatNumber(dashboardTiles.total_users)}</p>
          </div>
        </div>
        <div style={s.btnGroup}>
          <button style={{...s.btnOutlined, background: showFilters ? "#f8fafc" : "#fff"}} onClick={() => setShowFilters(!showFilters)}>
            <FiFilter size={14} /> {showFilters ? 'Hide' : 'Show'} Database Matrix
          </button>
          <button style={s.btnSave} onClick={fetchUserReportData} disabled={loading}>
            {loading ? <span className="spinner-border spinner-border-sm" /> : <BsArrowRepeat size={14} />} 
            {loading ? "Processing..." : "Sync Ecosystem"}
          </button>
        </div>
      </div>

      {/* Embedded Deep Filter Panel for Numeric Ranges */}
      {showFilters && (
        <div style={s.filterBox}>
          <div>
             <label style={s.label}>Minimum Fluidity</label>
             <input type="number" style={s.input} placeholder="₹ Wallet Floor" value={filters.minWalletBalance} onChange={e=>setFilters({...filters, minWalletBalance: e.target.value})} />
          </div>
          <div>
             <label style={s.label}>Referral Minimum</label>
             <input type="number" style={s.input} placeholder="Node Network count" value={filters.minTotalReferrals} onChange={e=>setFilters({...filters, minTotalReferrals: e.target.value})} />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end'}}>
             <button style={{...s.btnOutlined, width: '100%', justifyContent: 'center'}} onClick={() => setFilters({
                  status: '', role: '',
                  minWalletBalance: '', maxWalletBalance: '',
                  minTotalReferrals: '', maxTotalReferrals: '',
                  minBetAmount: '', maxBetAmount: ''
             })}>Clear Parameters</button>
          </div>
        </div>
      )}

      {/* Financial Executive Nodes */}
      <div style={s.summaryGrid}>
        <div style={{ ...s.sumCard, background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={s.sumLabel}>Wallet Reserves</span>
            <span style={s.sumIcon}><FiCreditCard size={20} /></span>
          </div>
          <div style={s.sumValue}>₹{formatNumber(dashboardTiles.total_wallet_balance)}</div>
        </div>

        <div style={{ ...s.sumCard, background: "linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={s.sumLabel}>Referral Base</span>
            <span style={s.sumIcon}><FiUserPlus size={20} /></span>
          </div>
          <div style={s.sumValue}>{formatNumber(dashboardTiles.total_referrals)}</div>
        </div>

        <div style={{ ...s.sumCard, background: "linear-gradient(135deg, #b91c1c 0%, #ef4444 100%)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={s.sumLabel}>Aggregate Bet</span>
            <span style={s.sumIcon}><FiTarget size={20} /></span>
          </div>
          <div style={s.sumValue}>₹{formatNumber(dashboardTiles.total_bet_amount)}</div>
        </div>

        <div style={{ ...s.sumCard, background: "linear-gradient(135deg, #d97706 0%, #fbbf24 100%)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={s.sumLabel}>Aggregate Win</span>
            <span style={s.sumIcon}><FiTrendingUp size={20} /></span>
          </div>
          <div style={s.sumValue}>₹{formatNumber(dashboardTiles.total_win_amount)}</div>
        </div>

        <div style={{ ...s.sumCard, background: "linear-gradient(135deg, #047857 0%, #10b981 100%)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={s.sumLabel}>Total Withdraw</span>
            <span style={s.sumIcon}><FiDownload size={20} /></span>
          </div>
          <div style={s.sumValue}>₹{formatNumber(dashboardTiles.total_withdraw_approved)}</div>
        </div>
      </div>

      {/* Main Table Layer */}
      <DataTable 
          columns={userCols} 
          data={filteredData || []} 
          toolbarExtra={
            <div style={{ display: 'flex', gap: 10 }}>
               <select style={{...s.input, width: 130, padding: "7px 12px", fontSize: "0.82rem"}} value={filters.status} onChange={e=>setFilters({...filters, status: e.target.value})}>
                 <option value="">All Statuses</option>
                 <option value="active">Active Only</option>
                 <option value="inactive">Inactive Only</option>
               </select>
               <select style={{...s.input, width: 130, padding: "7px 12px", fontSize: "0.82rem"}} value={filters.role} onChange={e=>setFilters({...filters, role: e.target.value})}>
                 <option value="">All Roles</option>
                 <option value="user">Users</option>
                 <option value="agent">Agents</option>
                 <option value="admin">Admins</option>
               </select>
            </div>
          }
      />

    </div>
  );
};

export default AllUserReportLayerList;
